"use client";

import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Braces, X } from "lucide-react";
import type { Edge } from "@xyflow/react";

import {
  graphNodesAtom,
  edgesAtom,
  nodeRegistryAtom,
  nodeDataMapAtom,
  dynamicNodeConfigsAtom,
  activeFlowIdAtom,
  pickerHighlightAtom,
  type PickerHighlight,
} from "@/store/editor-v2";
import { useSpotUser } from "@/utils/spotUser";
import { useSecretsSources } from "./useSecretsSources";
import type { SecretLookupEntry } from "./mentionSource";
import type { NodeDefinition } from "@/generated";
import { useVariableOptions } from "./VariablePicker";
import { buildOptionsByValue, type NodeOption } from "./variableOptions";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { VariablePickerPanel } from "./VariablePickerPanel";
import { VariableRefChip } from "./VariableRefChip";
import {
  resolveVariableRefStatus,
  type VariableRefStatus,
} from "./variableRefStatus";
import { Input } from "@/components/shadcn/input";
import { NoxusIconButton } from "@/components/UI/NoxusIconButton";
import { NumberInput } from "@/components/Form/NumberInputField";
import { DatePickerInput } from "@/components/Form/DatePickerField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Tooltip, TooltipTrigger } from "@/components/shadcn/tooltip";
import { BoundVariableTooltipContent } from "./BoundVariableTooltip";
import { useBoundSourceHighlight } from "./useBoundSourceHighlight";
import { cn } from "@/lib/utils";
import {
  formatTemplateToken,
  formatVarRefToken,
  isTemplateVariableToken,
  templateVariableLabel,
  WHOLE_VAR_REF_PATTERN,
} from "./variableRefs";

const refToken = formatVarRefToken;

const CLEAR_HIGHLIGHT: PickerHighlight = {
  sourceNodeId: undefined,
  sourceOutputName: undefined,
  targetNodeId: undefined,
  targetInputName: undefined,
};
const parseWholeRef = (s: string | undefined | null): string | null => {
  if (typeof s !== "string") return null;
  const m = s.match(WHOLE_VAR_REF_PATTERN);
  return m ? m[1] : null;
};

/**
 * Single-line bindable input, the small form of the bindable primitive.
 *
 * Every mode renders the same row: the control on the left and a separate
 * `{}` (braces) button on the right that opens the variable picker. Two modes
 * for the same `value: string` storage:
 *   - LITERAL: typed input matching `dataType` (text/number/bool select).
 *   - VARIABLE: when value is exactly `:var[<ref>]`, the control becomes a
 *     flat chip of the bound variable with an inline X that clears it, and the
 *     `{}` button stays, marked as bound.
 *
 * Pass `variableOnly` for fields that must reference a variable (e.g. a
 * condition's left-hand side): the literal control is dropped entirely and
 * the empty state becomes a "select variable" trigger.
 *
 * The picker filters variables to those compatible with `dataType`, so a
 * NUMBER field only offers number-typed sources, etc.
 *
 * For multi-line text with inline refs (`prompt :var[x] ...`) use the
 * `ComplexMarkdownField` instead — this widget is whole-string-only.
 */
export function BindableInput({
  nodeId,
  value,
  onChange,
  dataType,
  isList,
  enumOptions,
  placeholder,
  disabled,
  defaultToVariable,
  inputName,
  variableOnly,
  renderLiteral,
  templateVariables,
  integerOnly,
  allowNegative,
  isRequired,
  min,
  max,
  clearsToEmpty = true,
  onBlur,
  invalid = false,
  strictTypes = false,
}: {
  nodeId: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  /** Data type used to filter compatible variables in the picker. */
  dataType?: string;
  /** When true, the field expects a list: the picker offers only list sources
   * and a bound scalar is flagged as incompatible. */
  isList?: boolean;
  /** Allowed literal values (a JSON-schema `enum`): the literal control becomes
   * a select of exactly these instead of a free-text input. */
  enumOptions?: Array<string | { value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
  /** Auto-open the picker when the field is empty (sensible for fields that
   * are almost always bound to a variable, e.g. an OutputNode's value). */
  defaultToVariable?: boolean;
  /** Field key being bound — used to light up the target input on the canvas
   * while the picker is open (mirrors VariablePicker). */
  inputName?: string;
  /** Drop the literal control: the value must be a variable. The empty state
   * renders a "select variable" trigger instead of a typed input. */
  variableOnly?: boolean;
  /** Custom literal editor (e.g. a file picker) rendered in place of the
   * default typed input. The "bind to variable" braces button still sits
   * beside it, and variable mode (chip + picker) is unchanged. */
  renderLiteral?: React.ReactNode;
  /** Amber inset ring on the control itself (same look as an invalid
   * `Input`). A wrapper ring can't do this: the bound chip and the
   * variable-only trigger paint an opaque white box over anything behind. */
  invalid?: boolean;
  /** Offer only sources whose type matches exactly — no coerced matches — for
   * a field whose consumer has no coercion path (a typed table column). */
  strictTypes?: boolean;
  /** Playbook-local template variables (loop_item, prior step outputs).
   * Offered as a "Playbook" picker group; picking one splices the literal
   * `{{name}}` token into the text value instead of binding a `:var` ref —
   * the playbook executor substitutes it at run time. */
  templateVariables?: string[];
  /** Number fields only: the literal input accepts whole numbers — decimal
   * and exponent characters are rejected as typed. */
  integerOnly?: boolean;
  allowNegative?: boolean;
  isRequired?: boolean;
  /** Number fields only: floor/ceiling forwarded to NumberInput, which
   * enforces them on the stepper and clamps typed values on blur. */
  min?: number;
  max?: number;
  // An emptied number box stores "", which reads as "no value". A caller
  // whose field must always hold a number passes false to store "0" instead.
  clearsToEmpty?: boolean;
}) {
  const nodes = useAtomValue(graphNodesAtom);
  const edges = useAtomValue(edgesAtom) as Edge[];
  const nodeRegistry = useAtomValue(nodeRegistryAtom) as NodeDefinition[];
  const nodeDataMap = useAtomValue(nodeDataMapAtom);
  const dynamicNodeConfigs = useAtomValue(dynamicNodeConfigsAtom);
  const { spotGroup } = useSpotUser();
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  const { secrets } = useSecretsSources(spotGroup?.id, activeFlowId);
  const secretsByQualifiedId = useMemo(() => {
    const m = new Map<string, SecretLookupEntry>();
    for (const s of secrets) m.set(`${s.scope}:${s.id}`, s);
    return m;
  }, [secrets]);
  const allOptions = useVariableOptions(
    nodes,
    nodeId,
    edges,
    nodeRegistry,
    dataType,
    inputName,
    isList
  );
  const optionsByValue = useMemo(
    () => buildOptionsByValue(allOptions),
    [allOptions]
  );
  const compatibleNodes = useMemo(
    () => allOptions.filter((n) => n.hasCompatible),
    [allOptions]
  );
  // The shared panel renders every output it's given; drop the incompatible
  // ones here (the Command-based picker used to filter them at render time).
  const pickerOptions = useMemo(
    () =>
      compatibleNodes
        .map((node) => ({
          ...node,
          outputs: node.outputs.filter(
            (o) => o.compatible && (!strictTypes || o.compatibility === "ok")
          ),
        }))
        .filter((node) => node.outputs.length > 0),
    [compatibleNodes, strictTypes]
  );
  const templateGroup = useMemo<NodeOption | null>(() => {
    if (!templateVariables?.length) return null;
    return {
      nodeId: "__playbook__",
      nodeName: "Playbook",
      nodeColor: "#e0e7ff",
      nodeImage: undefined,
      outputs: templateVariables.map((name) => ({
        value: formatTemplateToken(name),
        outputName: name,
        outputLabel: templateVariableLabel(name),
        outputOriginalLabel: name,
        dataType: "str",
        isList: false,
        listDepth: 0,
        compatible: true,
        compatibility: "ok" as const,
      })),
      hasCompatible: true,
    };
  }, [templateVariables]);
  const allPickerOptions = useMemo(
    () => (templateGroup ? [...pickerOptions, templateGroup] : pickerOptions),
    [pickerOptions, templateGroup]
  );

  const [open, setOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const currentRef = parseWholeRef(value);
  const normalizedEnumOptions = (enumOptions ?? []).map((option) =>
    typeof option === "string"
      ? { value: option, label: option }
      : { value: option.value, label: option.label }
  );
  const selected = currentRef ? optionsByValue.get(currentRef) : undefined;

  const variableChipStatus = useMemo((): VariableRefStatus | null => {
    if (!currentRef) return null;
    if (selected) {
      return {
        kind: "valid",
        meta: {
          sourceLabel: selected.node.nodeName,
          sourceImage: selected.node.nodeImage,
          sourceIconName: selected.node.nodeIconName,
          sourceColor: selected.node.nodeColor,
          variableLabel: selected.output.outputLabel,
          dataType: selected.output.dataType,
          isList: selected.output.isList,
        },
      };
    }
    return resolveVariableRefStatus(currentRef, {
      nodeId,
      nodeDataMap,
      edges,
      nodeRegistry,
      dynamicNodeConfigs,
      secretsByQualifiedId,
    });
  }, [
    currentRef,
    selected,
    nodeId,
    nodeDataMap,
    edges,
    nodeRegistry,
    dynamicNodeConfigs,
    secretsByQualifiedId,
  ]);

  // Canvas highlight: while the picker is open, the target node (this field's
  // node) lights up, and hovering an output highlights its source node — same
  // behaviour as the standalone VariablePicker.
  const setPickerHighlight = useSetAtom(pickerHighlightAtom);
  const openHighlight = useCallback(
    () =>
      setPickerHighlight({
        sourceNodeId: undefined,
        sourceOutputName: undefined,
        targetNodeId: nodeId,
        targetInputName: inputName,
      }),
    [setPickerHighlight, nodeId, inputName]
  );
  const hoverSource = useCallback(
    (sourceNodeId: string | undefined, sourceOutputName: string | undefined) =>
      setPickerHighlight((prev) => ({
        ...prev,
        sourceNodeId,
        sourceOutputName,
      })),
    [setPickerHighlight]
  );

  // Hovering the bound field lights up the variable's origin node on the
  // canvas — same affordance as hovering an option in the picker menu.
  const {
    highlight: highlightBoundSource,
    unhighlight: unhighlightBoundSource,
  } = useBoundSourceHighlight(currentRef);
  // True while the picker was opened by typing `/` (vs. the `{}` button): in
  // that mode the menu must NOT steal focus, so the user can keep typing.
  // Tracked as a parameter of every open (not a separate setState) so a
  // non-slash re-open while already open can't inherit a stale slash state.
  const [openedViaSlash, setOpenedViaSlash] = useState(false);
  const handleOpenChange = useCallback(
    (next: boolean, viaSlash = false) => {
      setOpen(next);
      setOpenedViaSlash(next ? viaSlash : false);
      if (next) {
        setPickerSearch("");
        openHighlight();
      } else {
        setPickerHighlight(CLEAR_HIGHLIGHT);
      }
    },
    [openHighlight, setPickerHighlight]
  );
  useEffect(
    () => () => setPickerHighlight(CLEAR_HIGHLIGHT),
    [setPickerHighlight]
  );

  // Sensible-default: nodes whose value is almost always a variable (e.g. the
  // OutputNode's `value`) can opt in to the picker opening automatically when
  // the field is empty. Only fires once per empty-field session so the user
  // can still type a literal without the picker re-popping.
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (!defaultToVariable || autoOpened || disabled) return;
    if (value === "" || value == null) {
      setOpen(true);
      openHighlight();
      setAutoOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const literalInputRef = useRef<HTMLInputElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);

  const pick = (ref: string) => {
    if (isTemplateVariableToken(ref)) {
      const base = currentRef ? "" : value || "";
      const input = literalInputRef.current;
      if (!currentRef && input && typeof input.selectionStart === "number") {
        const start = input.selectionStart;
        const end = input.selectionEnd ?? start;
        onChange(base.slice(0, start) + ref + base.slice(end));
      } else {
        onChange(base + ref);
      }
      setOpen(false);
      setPickerHighlight(CLEAR_HIGHLIGHT);
      return;
    }
    onChange(refToken(ref));
    setOpen(false);
    setPickerHighlight(CLEAR_HIGHLIGHT);
  };
  const clear = () => {
    onChange("");
  };

  // Typing `/` in an empty literal field opens the variable picker, mirroring
  // the ComplexMarkdownField's slash menu. The `/` is NOT swallowed and focus
  // stays in the input (see `openedViaSlash`), so the user can keep typing a
  // literal — e.g. a folder path — and only mouse-pick a variable if they want.
  const handleSlashOpen = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "/" &&
      !value &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      !e.nativeEvent.isComposing
    ) {
      handleOpenChange(true, true);
    }
  };

  // Same panel as the ComplexMarkdownField's `/` menu and `{}` toolbar picker.
  // Anchored to the whole row, end-aligned, so its right edge meets the `{}`.
  const dropdown = (
    <PopoverContent
      className="p-0 w-[330px] z-50"
      align="end"
      sideOffset={6}
      // Opened by `/`: keep focus in the input so typing continues. Opened by
      // the `{}` button: let the search autofocus as usual.
      onOpenAutoFocus={(e) => {
        if (openedViaSlash) e.preventDefault();
      }}
      // The bound chip opens the picker but is not a Radix trigger, so a
      // pointerdown on it must not count as an outside dismissal.
      onInteractOutside={(e) => {
        const target = e.target instanceof Node ? e.target : null;
        if (target && chipRef.current?.contains(target)) e.preventDefault();
      }}
    >
      {allPickerOptions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center px-4">
          No compatible variables available.
        </p>
      ) : (
        <VariablePickerPanel
          options={allPickerOptions}
          search={pickerSearch}
          onSearchChange={setPickerSearch}
          autoFocusSearch={!openedViaSlash}
          onHoverOutput={hoverSource}
          onSelect={({ value: ref }) => pick(ref)}
          selectedValue={currentRef ?? undefined}
        />
      )}
    </PopoverContent>
  );

  const bindButton = (
    <PopoverTrigger asChild>
      <NoxusIconButton
        type="button"
        icon={Braces}
        variant="secondary"
        isDisabled={disabled}
        data-bound-variable={currentRef !== null}
        iconProps={{ style: { width: 14, height: 14, strokeWidth: 2.25 } }}
        className="data-[bound-variable=true]:border-blue-200 data-[bound-variable=true]:bg-blue-50 data-[bound-variable=true]:[&_svg]:text-blue-900"
        aria-label="Bind to variable"
        title="Bind to variable"
      />
    </PopoverTrigger>
  );

  const row = (control: React.ReactNode) => (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div data-bindable-field className="flex items-start gap-1.5">
          <div className="min-w-0 flex-1">{control}</div>
          {bindButton}
        </div>
      </PopoverAnchor>
      {dropdown}
    </Popover>
  );

  // VARIABLE MODE: the control becomes a flat chip. No nested box: the shell
  // is the only border, the chip blends in, and an inline X clears it.
  if (currentRef) {
    const chipMeta =
      variableChipStatus?.kind === "not_found"
        ? undefined
        : variableChipStatus?.meta;
    return row(
      <div
        ref={chipRef}
        className="relative"
        onMouseEnter={highlightBoundSource}
        onMouseLeave={unhighlightBoundSource}
      >
        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              data-bound
              data-state={open ? "open" : "closed"}
              onBlur={onBlur}
              onClick={() => handleOpenChange(!open)}
              className={cn(
                "group/bind flex h-8 w-full min-w-0 items-center rounded-md border border-input bg-white pl-1.5 pr-8 shadow-sm",
                invalid && "ring-1 ring-inset ring-amber-400/70",
                "cursor-pointer text-left transition-colors outline-none",
                "hover:bg-accent/40 data-[state=open]:ring-ring data-[state=open]:ring-1",
                "focus-visible:ring-ring focus-visible:ring-[1px] disabled:cursor-not-allowed disabled:opacity-50"
              )}
            >
              {variableChipStatus ? (
                <VariableRefChip
                  bare
                  size="sm"
                  relaxedWidth
                  status={variableChipStatus}
                />
              ) : null}
            </button>
          </TooltipTrigger>
          {!open && chipMeta && (
            <BoundVariableTooltipContent
              variableLabel={chipMeta.variableLabel}
              dataType={chipMeta.dataType}
              isList={chipMeta.isList}
              sourceLabel={chipMeta.sourceLabel}
              sourceImage={chipMeta.sourceImage}
              sourceIconName={chipMeta.sourceIconName}
              sourceColor={chipMeta.sourceColor}
              variableRef={currentRef}
              isSecret={chipMeta.isSecret}
            />
          )}
        </Tooltip>
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            clear();
          }}
          className={cn(
            "absolute right-1 top-1/2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded",
            "text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
          )}
          aria-label="Clear variable"
          title="Clear variable"
        >
          <X size={13} />
        </button>
      </div>
    );
  }

  // VARIABLE-ONLY EMPTY STATE: no literal control; just a trigger that opens
  // the picker. Used by fields that must reference a variable (e.g. a
  // condition's left-hand side).
  if (variableOnly) {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            onBlur={onBlur}
            className={cn(
              "group/bind flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-white px-2.5 shadow-sm",
              invalid && "ring-1 ring-inset ring-amber-400/70",
              "cursor-pointer text-left text-sm text-muted-foreground transition-colors outline-none",
              "hover:bg-blue-50 data-[state=open]:bg-blue-50 data-[state=open]:ring-ring data-[state=open]:ring-1",
              "focus-visible:ring-ring focus-visible:ring-[1px] disabled:cursor-not-allowed disabled:opacity-50"
            )}
          >
            <span className="truncate">
              {placeholder ?? "Select variable…"}
            </span>
            <Braces
              size={14}
              strokeWidth={2.25}
              className="shrink-0 text-muted-foreground/70 transition-colors group-hover/bind:text-blue-900 group-data-[state=open]/bind:text-blue-900"
            />
          </button>
        </PopoverTrigger>
        {dropdown}
      </Popover>
    );
  }

  // LITERAL MODE: typed input beside the "bind to variable" button.
  const parsedNumber = parseFloat(value);
  const numberValue = Number.isFinite(parsedNumber) ? parsedNumber : undefined;
  // A `{{template}}` value needs the text surface: NumberInput can't
  // represent it and would render an empty box over a set value.
  const hasTemplateValue = typeof value === "string" && value.includes("{{");
  // A caller-supplied literal editor always wins: `dataType` still drives which
  // variables the picker offers, so a control that is not the default for its
  // type (e.g. a Number *list* editor) needs both, the type filter and its own
  // editor. Without this guard `dataType="number"` would silently replace it
  // with a single-number input.
  const literalControl =
    renderLiteral ??
    (normalizedEnumOptions.length > 0 ? (
      <Select
        disabled={disabled}
        value={
          normalizedEnumOptions.some((option) => option.value === value)
            ? value
            : ""
        }
        onValueChange={onChange}
      >
        <SelectTrigger className="h-8 w-full" onBlur={onBlur}>
          <SelectValue placeholder={placeholder ?? "Select…"} />
        </SelectTrigger>
        <SelectContent>
          {normalizedEnumOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : dataType === "bool" ? (
      <Select
        disabled={disabled}
        value={value === "true" || value === "false" ? value : ""}
        onValueChange={onChange}
      >
        <SelectTrigger className="h-8 w-full" onBlur={onBlur}>
          <SelectValue placeholder={placeholder ?? "Select…"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>
    ) : dataType === "datetime" ? (
      <DatePickerInput
        value={value}
        onChange={onChange}
        onClear={clear}
        onBlur={onBlur}
        isDisabled={disabled}
        placeholder={placeholder}
        includeTime
      />
    ) : dataType === "number" && !hasTemplateValue ? (
      <NumberInput
        value={numberValue}
        onChange={(n) => onChange(String(n))}
        onClear={isRequired ? undefined : () => onChange("")}
        onEmptied={() => onChange(clearsToEmpty ? "" : "0")}
        onBlur={onBlur}
        onKeyDown={handleSlashOpen}
        isDisabled={disabled}
        placeholder={placeholder}
        step={integerOnly ? 1 : "any"}
        integer={integerOnly}
        allowNegative={allowNegative}
        min={min}
        max={max}
      />
    ) : (
      <Input
        ref={literalInputRef}
        disabled={disabled}
        value={value}
        placeholder={placeholder}
        type="text"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleSlashOpen}
        onBlur={onBlur}
        className="bg-white"
      />
    ));

  return row(literalControl);
}
